import { compile } from "path-to-regexp";

import { debug } from "./helpers";

export enum RoutePathName {
    home = "home",
    login = "login",
    notFound = "notFound",
    forgottenPassword = "forgottenPassword",
    resetPassword = "resetPassword",

    dashboard = "dashboard",
    biPerformedTourOrders = "biPerformedTourOrders",
    biSupportShipped = "biSupportShipped",
    biPunctualityRate = "biPunctualityRate",
    biFillingRate = "biFillingRate",
    biDeliveryConformityRate = "biDeliveryConformityRate",
    biPalletConformityRate = "biPalletConformityRate",
    biAverageTourOrdersPerDriverPerDay = "biAverageTourOrdersPerDriverPerDay",
    biReverseSupportRate = "biReverseSupportRate",
    biCarriersServiceRate = "biCarriersServiceRate",
    biPerformedDeliveries = "biPerformedDeliveries",
    biUnloadingTime = "biUnloadingTime",

    chooseHub = "chooseHub",
    tourOrdersList = "tourOrdersList",
    tourOrdersInProgressMap = "tourOrdersInProgressMap",
    tourOrderDetails = "tourOrderDetails",
    driversMonitoring = "driversMonitoring",
    driversDetails = "driversDetails",
    storesList = "storesList",
    carriersList = "carriersList",
    trailersList = "trailersList",
    driversList = "driversList",
    settings = "settings",
    usersList = "usersList",
    valueListItems = "valueListItems",
    warehousesAndPlatforms = "warehousesAndPlatforms",
    tourOrderAnomaliesList = "tourOrderAnomaliesList",
    shippingManagment = "shippingManagment",
    returnedSupports = "returnedSupports",

    // Packaging Portal
    storePortalTourOrdersList = "storePortalTourOrdersList",
    storePortalTourOrderDetails = "storePortalTourOrderDetails",
    packagingPortalReturnedSupports = "packagingPortalReturnedSupports",

    // Store Portal
    storePortalReturnedSupporList = "storePortalReturnedSupporList",

    // Super Admin
    applicationList = "applicationList",
    organizationList = "organizationList",
    valueListList = "valueListList",
    roleList = "roleList",
    jobList = "jobList",
    importedTourOrdersList = "importedTourOrdersList",
    trimbleLicenseManagment = "trimbleLicenseManagment",
}

export type Routes = {
    [r in RoutePathName]: string;
};

export const routes: Routes = {
    [RoutePathName.home]: "/",
    [RoutePathName.login]: "/login",
    [RoutePathName.notFound]: "/404",
    [RoutePathName.forgottenPassword]: "/mot-de-passe-oublie",
    [RoutePathName.resetPassword]: "/reset-password",

    // Dashboard
    [RoutePathName.dashboard]: "/dashboard",
    [RoutePathName.biPerformedTourOrders]: "/dashboard/tours-effectues",
    [RoutePathName.biPunctualityRate]: "/dashboard/taux-ponctualite",
    [RoutePathName.biFillingRate]: "/dashboard/taux-remplissage",
    [RoutePathName.biDeliveryConformityRate]: "/dashboard/taux-conformite-livraison",
    [RoutePathName.biPalletConformityRate]: "/dashboard/taux-conformite-palette",
    [RoutePathName.biAverageTourOrdersPerDriverPerDay]: "/dashboard/moyenne-tours-chauffeur-jour",
    [RoutePathName.biReverseSupportRate]: "/dashboard/taux-support-retour",
    [RoutePathName.biCarriersServiceRate]: "/dashboard/taux-service-transporteurs",
    [RoutePathName.biSupportShipped]: "/dashboard/emballages-expedies",
    [RoutePathName.biPerformedDeliveries]: "/dashboard/livraisons-effectuees",
    [RoutePathName.biUnloadingTime]: "/dashboard/temps-dechargement",

    // Management
    [RoutePathName.chooseHub]: "/centrale",
    [RoutePathName.tourOrdersList]: "/pilotage/tours",
    [RoutePathName.tourOrdersInProgressMap]: "/pilotage/tours/carte-ot-en-cours",
    [RoutePathName.tourOrderDetails]: "/pilotage/tours/:tourOrderId",
    [RoutePathName.driversMonitoring]: "/pilotage/suivi-chauffeurs",
    [RoutePathName.driversDetails]: "/pilotage/suivi-chauffeurs/:driverId",
    [RoutePathName.shippingManagment]: "/suivi-expeditions",
    [RoutePathName.storesList]: "/magasins",
    [RoutePathName.carriersList]: "/transporteurs",
    [RoutePathName.trailersList]: "/remorques",
    [RoutePathName.driversList]: "/chauffeurs",
    [RoutePathName.settings]: "/parametres/listes-de-valeurs",
    [RoutePathName.usersList]: "/parametres/utilisateurs",
    [RoutePathName.warehousesAndPlatforms]: "/parametres/listes-de-valeurs/warehousesAndPlatforms",
    [RoutePathName.valueListItems]: "/parametres/listes-de-valeurs/:valueListSlug",
    [RoutePathName.returnedSupports]: "/pilotage/emballages-en-retour",
    [RoutePathName.tourOrderAnomaliesList]: "/pilotage/anomalies",

    // Packaging Portal
    [RoutePathName.packagingPortalReturnedSupports]: "/emballages/emballages-en-retour",

    // Store Portal
    [RoutePathName.storePortalTourOrdersList]: "/magasin/tours",
    [RoutePathName.storePortalTourOrderDetails]: "/magasin/tours/:tourOrderId",
    [RoutePathName.storePortalReturnedSupporList]: "/magasin/emballages-en-retour",

    // Super Admin
    [RoutePathName.organizationList]: "/super-admin/organizations",
    [RoutePathName.applicationList]: "/super-admin/applications",
    [RoutePathName.valueListList]: "/super-admin/values-lists",
    [RoutePathName.roleList]: "/super-admin/roles",
    [RoutePathName.jobList]: "/super-admin/jobs",
    [RoutePathName.importedTourOrdersList]: "/super-admin/imported-tour-orders",
    [RoutePathName.trimbleLicenseManagment]: "/super-admin/trimble-license-management",
};

export interface RouteParams {
    [param: string]: string | number;
}

// returns raw react-router string path eg: /user/:id
export const getRawRoute = (path: RoutePathName) => {
    if (!routes[path]) {
        debug.error(`[getRawRoute] Route not found for path "${path}", returning /404.`);
        return "/404";
    } else {
        return routes[path];
    }
};

// returns real-world path eg: /user/1337
export const getRoute = (
    path: RoutePathName,
    params?: RouteParams,
    queryParams?: string | string[][] | Record<string, string> | URLSearchParams | undefined,
    anchor?: string,
) => {
    let route = getRawRoute(path);
    const compiler = compile(route, { encode: encodeURIComponent });

    try {
        route = compiler(params);
    } catch (error) {
        debug.error("[getRoute] route compile error :", error);
    }

    if (queryParams && Object.keys(queryParams).length) {
        const urlQueryParams = new URLSearchParams(queryParams);
        route += `?${urlQueryParams.toString()}`;
    }

    if (anchor) {
        route = `${route}#${anchor}`;
    }

    return route;
};
